import {
  QueryClient,
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { SubdistrictData } from "./subdistrict";
import { z } from "zod";
import { MAX_5_CHARACTERS_MESSAGE, REQUIRED_MESSAGE } from "@/utils/constant";
import { axiosInstance } from "@/utils/lib/axios";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { Paginated } from "./types";
import { generateParams } from "@/utils/helper/generateParams";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { stringRange } from "@/utils/helper/refinement";

const villagePayloadSchema = z.object({
  villageCode: z
    .string()
    .max(5, { message: MAX_5_CHARACTERS_MESSAGE })
    .nullable()
    .optional(),
  villageName: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine(...stringRange(4, 50)),
  subdistrict: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
  postalCode: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .max(5, { message: MAX_5_CHARACTERS_MESSAGE })
    .regex(/^-?\d*$/, { message: "Must be a number." }),
});

type VillagePayloadSchema = z.infer<typeof villagePayloadSchema>;

type VillageData = {
  id: string;
  villageCode: string | null;
  villageName: string;
  subdistrict: SubdistrictData;
  postalCode: string;
};

/* ============ *
 * READ village *
 * ============ */

type VillagesQueryKey = DataConfig;
interface VillageList extends Paginated {
  content: VillageData[];
}
async function getVillages(
  queryFunctionContext: QueryFunctionContext,
): Promise<VillageList> {
  const { queryKey } = queryFunctionContext;
  const [_, villagesQueryKey] = queryKey;
  const params = generateParams(villagesQueryKey as VillagesQueryKey);

  const response = await axiosInstance.get(`/api/crud/village${params}`);
  return response.data.data;
}

function useVillages(
  villagesQueryKey: VillagesQueryKey,
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["villages", villagesQueryKey],
    queryFn: getVillages,
  });
}

interface VillageQueryKey {
  id: string;
}

async function getVillage(
  queryFunctionContext: QueryFunctionContext,
): Promise<VillageData> {
  const { queryKey } = queryFunctionContext;
  const [_, villagesQueryKey] = queryKey;
  const { id } = villagesQueryKey as VillageQueryKey;

  const response = await axiosInstance.get(`/api/crud/village/${id}`);
  return response.data.data;
}

function useVillage(villageQueryKey: VillageQueryKey) {
  const { id } = villageQueryKey;
  return useQuery({
    queryKey: ["villages", { id }],
    queryFn: getVillage,
  });
}

function villageLoader(queryClient: QueryClient): LoaderFunction<VillageData> {
  return async ({ params }: LoaderFunctionArgs<{ id: string }>) => {
    const village = await queryClient.ensureQueryData({
      queryKey: ["villages", { id: params.id }],
      queryFn: getVillage,
    });
    return village;
  };
}

/* ============== *
 * CREATE village *
 * ============== */

async function createVillage(data: VillagePayloadSchema): Promise<VillageData> {
  const response = await axiosInstance.post("/api/crud/village", data);
  return response.data;
}

function useVillageCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: VillagePayloadSchema }) =>
      createVillage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["villages"],
      });
    },
  });
}

/* ============== *
 * UPDATE village *
 * ============== */

async function editVillage(
  id: string,
  data: VillagePayloadSchema,
): Promise<VillageData> {
  const response = await axiosInstance.post(`/api/crud/village/${id}`, data);
  return response.data;
}

function useVillageEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: VillagePayloadSchema }) =>
      editVillage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["villages"],
      });
    },
  });
}

/* ============== *
 * DELETE village *
 * ============== */

async function deleteVillage(id: string): Promise<VillageData> {
  const response = await axiosInstance.delete(`/api/crud/village/${id}`);
  return response.data;
}

function useVillageDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteVillage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["villages"],
      });
    },
  });
}

async function deleteBulkVillage(ids: string[]): Promise<VillageData[]> {
  return await Promise.all(ids.map((id) => deleteVillage(id)));
}

function useVillageBulkDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids }: { ids: string[] }) => deleteBulkVillage(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["villages"],
      });
    },
  });
}

/* ============== *
 * DOWNLOAD village *
 * ============== */

async function waitForMediaCompletion(
  url: string,
  interval = 2000,
): Promise<{ status: string; path?: string; originalFileName?: string }> {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const {
      data: {
        data: { status, path, originalFileName },
      },
    } = await axiosInstance.get(`${url}`);

    if (status === "SUCCESS" || status === "ERROR") {
      return {
        status,
        path,
        originalFileName: originalFileName ?? "list-village.xlsx",
      };
    }

    await new Promise((resolve) => setTimeout(resolve, interval));
  }
}

async function exportVillage(
  config: DataConfig = {},
  isDownload: boolean = false,
  onDownload: boolean = false,
): Promise<{ status: string; fileName?: string }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let createResponse: any = {};
  if (!isDownload) {
    createResponse = await axiosInstance.get("/api/crud/village/excel", {
      params: { ...config },
    });
    localStorage.setItem("isDownloadVillage", new Date().toISOString());
  }

  const downloadParams = {
    className: "com.alurkerja.crud.masterdatavillage.MasterDataVillage",
    entityId: config.entityId,
  };
  const { status, path, originalFileName } = await waitForMediaCompletion(
    !isDownload
      ? `/api/crud/media/${createResponse.data.data.id}`
      : `/api/crud/media/last${generateParams(downloadParams)}`,
  );

  if (onDownload && status === "SUCCESS") {
    const downloadResponse = await axiosInstance.get(
      "/api/crud/media/download",
      {
        params: { path, fileName: originalFileName, entityId: config.entityId },
        responseType: "blob",
      },
    );

    downloadFile(
      new Blob([downloadResponse.data]),
      originalFileName ?? "village-list.xlsx",
    );
    localStorage.removeItem("isDownloadVillage");
    return { status: "IDLE" };
  } else {
    return { status };
  }
}

function downloadFile(blobData: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blobData);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function useExportVillage() {
  return useMutation({
    mutationFn: async ({
      config,
      isDownload,
      onDownload,
    }: {
      config?: DataConfig;
      isDownload?: boolean;
      onDownload?: boolean;
    }) => {
      return await exportVillage(config, isDownload, onDownload);
    },
  });
}

export type { VillagePayloadSchema, VillageData };
export {
  villagePayloadSchema,
  villageLoader,
  useVillages,
  useVillage,
  useVillageCreate,
  useVillageEdit,
  useVillageDelete,
  useVillageBulkDelete,
  useExportVillage,
  // Exporting for testing purposes
  exportVillage,
  downloadFile,
  waitForMediaCompletion,
};
